import { useEffect, useRef, useMemo, useState, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Mic } from "lucide-react";
import { useAuth } from "../../lib/AuthContext";
import {
  useActiveGroups, useAwaitingReply, useStakeholders, useAllSentimentHistory,
  useSendGroupMessage, useGroupMessageLog, useUploadChatAttachments,
  useSendFeedback, useSendWorkshopContacts,
  useSendNextServiceDue, useSendPreDeliveryFeedback, useSendWelcomeMessage,
  useSendInviteReminder, useVehicleCatalog, useMemberChanges,
} from "../../lib/api";
import { toQueueVehicle } from "../../lib/queueMetrics";
import { parseCreatedAt, inRange } from "../../lib/dates";
import { nb, isWaGroupDeleted, waDeletedDay, STATUS_META } from "../../lib/journey";
import { customerInGroup } from "../../lib/members";
import { Icon } from "../../components/msd/Icon";
import { CARD, Chip, DISP, IconBtn, cx } from "../../components/msd/ui";
import { MessageList } from "../../components/chat/MessageList";
import { normalizeGroupName } from "../../lib/groupName";
import {
  BUCKETS,
  DEFAULT_BUCKET,
  advisorOptions,
  cacheMemberSummary,
  deskEscalationsForMe,
  matchesAdvisor,
  openRowFromSearch,
  shouldShowEscalationRecommendation,
} from "./deskBuckets";
import { useTenantTerms } from "../../lib/TenantConfigContext";
import LeftRail from "./LeftRail";
import RightRail from "./RightRail";
import Workspace from "./Workspace";
import { useDeskSummary } from "./useDeskSummary";
import { STAGE_META } from "../../lib/complaints";
import ConductNotice from "./ConductNotice";
import { AiPencilButton, AiSummaryCard, useAiSummary } from "./AiSummary";
import VehicleThumb from "@packs/automotive/web/pages/ServiceDesk/VehicleThumb";

function formatChatSubtitle(g) {
  const parts = [];
  if (nb(g["Customer Name"])) parts.push(g["Customer Name"]);
  const status = g["Status"];
  if (status === "Created") parts.push("Active");
  else if (nb(status)) parts.push(STATUS_META[status]?.label || status);
  // Which WhatsApp number this group runs on — a brand that has changed
  // numbers keeps older groups on the old one.
  if (nb(g["Client Number"])) parts.push(`+${String(g["Client Number"]).replace(/^\+/, "")}`);
  return parts.join(" · ");
}

// .hbtn — the round, borderless buttons in the chat header
const HBTN =
  "h-9 w-9 shrink-0 items-center justify-center rounded-full border-0 bg-transparent text-msd-royal hover:bg-msd-royal-soft";

const SENTIMENT_TONE = { Positive: "green", Negative: "red" };

function ChatHeader({ group, catalog, summary, onOpenSection, onBack, onDetails }) {
  return (
    <header
      className="relative z-[2] shrink-0 bg-[rgba(255,255,255,.82)] px-[18px] py-3 shadow-[0_1px_0_rgba(16,24,40,.04),0_6px_16px_-10px_rgba(16,24,40,.18)] backdrop-blur-[20px] backdrop-saturate-[180%] max-[860px]:px-3 max-[860px]:py-[10px] [:root[data-theme=dark]_&]:bg-[rgba(18,24,48,.82)]"
      data-testid="desk-chat-header"
    >
      <div className="flex items-center gap-3">
        <button type="button" onClick={onBack} aria-label="Back to list" className={cx(HBTN, "hidden max-[860px]:inline-flex")}>
          <Icon name="back" size={22} stroke={2.2} />
        </button>
        <VehicleThumb group={group} catalog={catalog} name={group["Customer Name"]} round />
        <div className="min-w-0 grow">
          <h2 className={cx("m-0 truncate text-[17px] font-semibold text-msd-ink", DISP)}>
            {group["Group Name"] || group["Customer Name"] || "—"}
          </h2>
          <p className="m-0 truncate text-[13px] text-msd-muted">{formatChatSubtitle(group)}</p>
        </div>
        {onDetails && (
          <button type="button" onClick={onDetails} aria-label="Details" className={cx(HBTN, "hidden max-[1280px]:inline-flex")}>
            <Icon name="info" size={21} stroke={1.9} />
          </button>
        )}
      </div>
      {summary && (
        <div className="mt-[10px] flex flex-wrap gap-[6px] pl-14">
          <Chip
            data-testid="desk-chip-sentiment"
            tone={SENTIMENT_TONE[summary.sentiment] || "grey"}
            onClick={() => onOpenSection("sentiment")}
          >
            {summary.sentiment || "No sentiment"}
          </Chip>
          {!customerInGroup(summary.members) && (
            <Chip data-testid="desk-chip-customer-not-joined" tone="orange" icon="userx" onClick={() => onOpenSection("members")}>
              Customer not joined
            </Chip>
          )}
          {summary.openComplaints > 0 && (
            <Chip data-testid="desk-chip-complaints" tone="red" icon="warn" onClick={() => onOpenSection("complaints")}>
              Complaint · {STAGE_META[summary.complaintStage]?.label || "Open"}
            </Chip>
          )}
          {summary.conductOpen > 0 && (
            <Chip data-testid="desk-chip-conduct" tone="red" icon="warn" onClick={() => onOpenSection("conduct")}>
              Conduct · {summary.conductOpen}
            </Chip>
          )}
          {summary.pendingMembers > 0 && (
            <Chip data-testid="desk-chip-members" onClick={() => onOpenSection("members")}>
              {summary.pendingMembers} not joined
            </Chip>
          )}
          <Chip data-testid="desk-chip-journey" tone="blue" onClick={() => onOpenSection("journey")}>
            {summary.current ? summary.current.label : "Journey"} · {summary.done}/{summary.steps.length}
          </Chip>
        </div>
      )}
    </header>
  );
}

// .bar — the strip above the composer: a solid icon tile, a title with one
// line under it, and the action on the right.
const TBTN =
  "h-8 shrink-0 whitespace-nowrap rounded-full border-0 px-[14px] text-[13.5px] font-semibold text-white shadow-[0_1px_2px_rgba(16,24,40,.16)] hover:brightness-[.97] disabled:cursor-not-allowed disabled:opacity-60";

function Bar({ icon, tile, title, sub, testid, children }) {
  return (
    <div
      className="mx-[18px] mb-[10px] flex shrink-0 items-center gap-3 rounded-[14px] bg-msd-surface p-[10px] text-[14.5px] shadow-msd-e2 max-[860px]:mx-[10px]"
      data-testid={testid}
    >
      <span className={cx("flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-[9px] text-white", tile)}>
        <Icon name={icon} size={18} stroke={2} />
      </span>
      <div className="min-w-0 grow">
        <b className="block truncate font-semibold text-msd-ink">{title}</b>
        {sub ? <span className="block truncate text-[13px] text-msd-muted">{sub}</span> : null}
      </div>
      {children}
    </div>
  );
}

function EscalationBanner({ escalation, group, onResolve, onAssign }) {
  const sentiment = group?.["Sentiment"] || "Negative";
  const isOpen = escalation?.status === "open";

  if (!isOpen) return null;

  return (
    <Bar
      icon="warn"
      tile="bg-msd-brick"
      title="Escalation open"
      sub={`Sentiment ${String(sentiment).toLowerCase()}`}
      testid="desk-escalation-banner"
    >
      <button type="button" onClick={onResolve} className={cx(TBTN, "bg-msd-btn")} data-testid="desk-escalation-resolve">
        Resolve
      </button>
      <button
        type="button"
        onClick={onAssign}
        disabled
        title="Assignment persistence arrives in a later phase"
        className="h-8 shrink-0 whitespace-nowrap rounded-full border-0 bg-msd-surface px-[14px] text-[13.5px] font-semibold text-msd-ink shadow-msd-e1 disabled:cursor-not-allowed disabled:opacity-45"
        data-testid="desk-escalation-assign"
      >
        Assign
      </button>
    </Bar>
  );
}

// .plusb -- the round grey button to the left of the message box
const PLUSB =
  "mb-[2px] flex h-9 w-9 shrink-0 items-center justify-center rounded-full border-0 bg-[rgba(120,120,128,.14)] text-msd-muted disabled:cursor-not-allowed disabled:opacity-45";

function fmtDuration(totalSeconds) {
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

/**
 * Voice notes from the desk. Records with MediaRecorder, then hands the blob
 * to the same upload-then-send path an attachment uses.
 *
 * The recorded type is passed through explicitly rather than detected from the
 * filename: MediaRecorder produces audio/webm on Chromium, and .webm is also a
 * video container, so a voice note would otherwise reach the customer as a
 * silent video.
 *
 * The microphone track is stopped on every exit path, including unmount and a
 * failed send — a live track leaves the browser's recording indicator on, which
 * looks like the dashboard is listening after the advisor has moved on.
 */
function MicRecorder({ disabled, onRecorded, onBusyChange }) {
  const [recording, setRecording] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  const releaseMic = useCallback(() => {
    const rec = recorderRef.current;
    rec?.stream?.getTracks?.().forEach((t) => t.stop());
    recorderRef.current = null;
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  useEffect(() => releaseMic, [releaseMic]);

  const supported =
    typeof window !== "undefined" &&
    typeof window.MediaRecorder !== "undefined" &&
    Boolean(navigator?.mediaDevices?.getUserMedia);

  async function start() {
    if (!supported) {
      toast.error("This browser cannot record audio.");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      // WhatsApp renders its own voice-note bubble only for audio/ogg with the
      // opus codec; anything else arrives as a file to download. Firefox can
      // record ogg/opus directly, Chromium cannot and falls back to webm — so
      // ask for the right one and take what we are given. Turning a webm into
      // a real voice note needs a transcode, which has to happen server-side.
      const preferred = ["audio/ogg;codecs=opus", "audio/ogg", "audio/webm;codecs=opus", "audio/webm"];
      const mimeType = preferred.find((t) => window.MediaRecorder.isTypeSupported?.(t));
      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      chunksRef.current = [];
      recorder.ondataavailable = (e) => {
        if (e.data?.size) chunksRef.current.push(e.data);
      };
      recorder.onstop = async () => {
        const type = recorder.mimeType || "audio/webm";
        const blob = new Blob(chunksRef.current, { type });
        releaseMic();
        setRecording(false);
        setSeconds(0);
        // A stray tap produces a fraction of a second of silence; sending it
        // costs a WhatsApp message and tells the customer nothing.
        if (blob.size < 1024) {
          toast.error("Recording too short — hold to record a voice note.");
          return;
        }
        const ext = type.includes("mp4") ? "m4a" : type.includes("ogg") ? "ogg" : "webm";
        const file = new File([blob], `voice-note-${Date.now()}.${ext}`, { type });
        await onRecorded(file);
      };
      recorderRef.current = recorder;
      recorder.start();
      setRecording(true);
      setSeconds(0);
      timerRef.current = setInterval(() => setSeconds((s) => s + 1), 1000);
    } catch (err) {
      releaseMic();
      setRecording(false);
      toast.error(
        err?.name === "NotAllowedError"
          ? "Microphone blocked — allow access in the browser to record."
          : "Could not start recording."
      );
    }
  }

  function stop() {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    onBusyChange?.("Sending voice note…");
    try {
      recorderRef.current?.stop();
    } catch {
      releaseMic();
      setRecording(false);
      onBusyChange?.(null);
    }
  }

  function cancel() {
    const rec = recorderRef.current;
    if (rec) rec.onstop = null; // drop the blob rather than sending it
    releaseMic();
    setRecording(false);
    setSeconds(0);
  }

  if (!supported) return null;

  if (recording) {
    return (
      <div className="mb-[2px] flex h-9 shrink-0 items-center gap-2" data-testid="desk-mic-recording">
        <span className="inline-flex h-7 items-center gap-[6px] rounded-full bg-[rgba(120,120,128,.12)] px-3 text-[13px] font-medium text-msd-ink [:root[data-theme=dark]_&]:bg-white/10">
          <span className="h-[7px] w-[7px] animate-pulse rounded-full bg-[#F04438]" />
          {fmtDuration(seconds)}
        </span>
        <button
          type="button"
          onClick={cancel}
          className="border-0 bg-transparent p-0 text-[13px] font-semibold text-msd-muted hover:text-msd-ink"
          data-testid="desk-mic-cancel"
        >
          Cancel
        </button>
        <button type="button" onClick={stop} className={cx(TBTN, "bg-[#F04438]")} data-testid="desk-mic-stop">
          Send
        </button>
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={start}
      disabled={disabled}
      data-testid="desk-mic"
      aria-label="Record a voice note"
      title="Record a voice note"
      className={PLUSB}
    >
      <Mic size={18} />
    </button>
  );
}

function ChatCompose({ group, row, customerNotJoined }) {
  const [draft, setDraft] = useState("");
  const [uploading, setUploading] = useState(false);
  // What is in flight, for the status line above the composer. A spinner on
  // the send button alone said nothing useful during a multi-file upload:
  // several files can take many seconds, the thread does not update until each
  // one lands, and the only feedback was one 16px spinner — long enough that
  // people press send again or assume it has hung.
  const [progress, setProgress] = useState(null);
  // Set when the API refuses a message for how it reads; see ConductNotice.
  const [conductNotice, setConductNotice] = useState(null);
  const send = useSendGroupMessage();
  const uploadAttachments = useUploadChatAttachments();
  const sendInvite = useSendInviteReminder();
  const hasGroupId = nb(group?.["Group ID"]);
  const aiSummary = useAiSummary(row);
  const closeAiSummary = aiSummary.close;

  useEffect(() => {
    setDraft("");
    closeAiSummary();
  }, [row]); // eslint-disable-line react-hooks/exhaustive-deps

  const sending = send.isPending || uploading;
  // The customer is who the group exists for -- a message typed before they
  // join reaches everyone else and gets lost by the time they finally see
  // it. The composer stays disabled and the send button becomes the invite
  // action until they're actually in the group.
  const blocked = !group || !hasGroupId || customerNotJoined;
  const canSend = Boolean(group && row && hasGroupId && !customerNotJoined && draft.trim() && !sending);

  function handleSendInviteReminder() {
    if (!group || sendInvite.isPending) return;
    sendInvite.mutate(group["_row"], {
      onSuccess: (data) => {
        const n = data.total_sent ?? 0;
        toast.success(n > 0 ? `Invite sent to ${n} member${n === 1 ? "" : "s"}.` : "No pending members to remind.");
      },
      onError: (err) => toast.error(err?.response?.data?.detail || err?.message || "Failed to send reminder."),
    });
  }

  async function handleSend() {
    const text = draft.trim();
    if (!row || !text) return;
    try {
      setProgress({ label: "Sending message…" });
      await send.mutateAsync({ row, text, messageType: "text" });
      setDraft("");
      toast.success("Message sent to WhatsApp group.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail?.code === "staff_message_blocked") {
        setConductNotice(detail);
      } else {
        toast.error(typeof detail === "string" ? detail : err?.message || "Failed to send message.");
      }
    } finally {
      setProgress(null);
    }
  }

  // Voice notes reuse the attachment path, but are sent as "audio" media: a
  // .webm name alone could not be told apart from a video.
  async function handleRecorded(file) {
    if (!row) return;
    try {
      setUploading(true);
      const { urls = [] } = await uploadAttachments.mutateAsync({ row, files: [file] });
      if (urls[0]) {
        await send.mutateAsync({ row, text: urls[0], messageType: "audio" });
        toast.success("Voice note sent to WhatsApp group.");
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : err?.message || "Could not send the voice note.");
    } finally {
      setUploading(false);
      setProgress(null);
    }
  }

  function onKeyDown(e) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (canSend) handleSend();
    }
  }

  const placeholder = !group
    ? "Select a group to message..."
    : !hasGroupId
      ? "Group not ready — no WhatsApp Group ID yet"
      : customerNotJoined
        ? "Customer hasn't joined the group yet"
        : "WhatsApp message";

  return (
    <div className="mx-[18px] mb-4 flex shrink-0 flex-col gap-2 max-[860px]:mx-[10px]">
      <ConductNotice notice={conductNotice} onClose={() => setConductNotice(null)} />
      <AiSummaryCard summary={aiSummary} />
      {progress && (
        <div
          className="flex items-center gap-2 rounded-xl bg-msd-royal-soft px-3 py-2"
          data-testid="desk-send-progress"
          role="status"
          aria-live="polite"
        >
          <Loader2 size={14} className="shrink-0 animate-spin text-msd-royal" />
          <span className="min-w-0 flex-1 truncate text-[13px] font-medium text-msd-ink">
            {progress.label}
          </span>
          {progress.step && (
            <span className="shrink-0 text-[12px] font-semibold text-msd-royal">{progress.step}</span>
          )}
        </div>
      )}
      {/* The round grey button records a voice note; the box holds the text and
          the one filled button that dispatches it, so send is unmistakably the
          primary action. Photos and videos are not sent from here -- they go
          out once, from the Inventory step. */}
      <div className="flex items-end gap-[10px]">
        <MicRecorder
          disabled={blocked || sending}
          onRecorded={handleRecorded}
          onBusyChange={(label) => setProgress(label ? { label } : null)}
        />
        <div className="flex min-h-10 min-w-0 grow items-center gap-[6px] rounded-[20px] bg-msd-surface pl-[14px] pr-1 shadow-msd-e1">
          <AiPencilButton onClick={aiSummary.request} disabled={!row} busy={aiSummary.loading} />
          <input
            type="text"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={onKeyDown}
            disabled={blocked || sending}
            placeholder={placeholder}
            aria-label="Message"
            data-testid="desk-chat-input"
            className="min-w-0 grow border-0 bg-transparent py-[9px] text-[15.5px] text-msd-ink outline-none placeholder:text-msd-faint disabled:cursor-not-allowed"
          />
          {!customerNotJoined && (
            <button
              type="button"
              onClick={handleSend}
              disabled={!canSend}
              data-testid="desk-chat-send"
              aria-label="Send message"
              className="flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-full border-0 bg-msd-royal text-white disabled:cursor-not-allowed disabled:bg-[rgba(120,120,128,.2)]"
            >
              {sending ? <Loader2 size={16} className="animate-spin" /> : <Icon name="arrowup" size={16} stroke={2.6} />}
            </button>
          )}
        </div>
        {customerNotJoined && (
          <button
            type="button"
            onClick={handleSendInviteReminder}
            disabled={sendInvite.isPending}
            data-testid="desk-chat-send-invite-reminder"
            aria-label="Send invite reminder"
            title="Customer hasn't joined — send them a reminder to join the group"
            className={cx(TBTN, "mb-1 inline-flex items-center gap-[6px] bg-[#F79009]")}
          >
            {sendInvite.isPending ? <Loader2 size={15} className="animate-spin" /> : <Icon name="userx" size={15} stroke={2.2} />}
            Send invite reminder
          </button>
        )}
      </div>
    </div>
  );
}

// Takes the composer's place once the WhatsApp group is deleted: the history
// above stays readable, but there is no group left to send into.
function GroupDeletedNotice({ group, className }) {
  return (
    <div
      role="status"
      data-testid="desk-group-deleted"
      className={cx("flex items-center gap-2 rounded-xl bg-msd-soft px-3 py-[10px] text-[13.5px] text-msd-muted", className)}
    >
      <Icon name="lock" size={15} />
      <span>
        This WhatsApp group was deleted on <b className="text-msd-ink">{waDeletedDay(group)}</b>. The chat is read-only.
      </span>
    </div>
  );
}

// ─── Chat helpers ──────────────────────────────────────────────────────────
// Shared with the Journey page's ChatHistoryPanel — see components/chat/.

function MessageTimeline({ group, row, stakeholders }) {
  const { data, isLoading } = useGroupMessageLog(row);
  const messages = data?.rows || [];
  const { data: memberChanges } = useMemberChanges(row);
  const stakeholdersWithAdded = useMemo(
    () => [
      ...(stakeholders || []),
      ...(memberChanges?.added || []).map((a) => ({ name: a.name, number: a.number, role: a.role })),
    ],
    [stakeholders, memberChanges]
  );
  const myNumber = group?.["Service Advisor Number"] || "";

  const isOwn = useCallback((msg) => {
    const saDigits = myNumber.replace(/\D/g, "").slice(-10);
    const msgDigits = (msg.sender_number || "").replace(/\D/g, "").slice(-10);
    return msg.sender_role === "sa" && Boolean(saDigits) && msgDigits === saDigits;
  }, [myNumber]);

  // The desk shows the conversation as it happened — everything, in order.
  //
  // It kept a customer-plus-SA filter once, which dropped every other staff
  // role: a WM or CCM could answer the customer in the group and the advisor
  // would see the question sitting there apparently unanswered. It then still
  // dropped the automation, which was just as wrong in a quieter way — the
  // templates are most of what the customer received, so the desk showed one
  // stray "Lets get started" where WhatsApp showed a vehicle-received notice,
  // a repair order and an estimate around it. An advisor comparing the two
  // cannot tell what the customer has already been told.
  //
  // So: no filter. Automated sends render as a centred pill rather than a
  // bubble, which is what keeps them legible as not-written-by-a-colleague.
  return (
    <MessageList
      messages={messages}
      group={group}
      stakeholders={stakeholdersWithAdded}
      isOwn={isOwn}
      isLoading={isLoading}
    />
  );
}

// Stage -> the one-click template send that moves the journey forward, for
// the stages that have one. A stage with no entry here (an upload/approval
// stage, e.g. Estimate, Job Card, Parts Approval) has no one-click send;
// its recommendation opens the Service Journey panel instead of guessing
// at a send.
const NEXT_STAGE_SEND = {
  initial: { templateKey: "workshop_contacts", label: "Send Escalation Metrics" },
  welcome: { templateKey: "welcome", label: "Send welcome message" },
  pre_delivery_feedback: { templateKey: "pre_delivery_feedback", label: "Send pre-delivery feedback" },
  next_service_due: { templateKey: "next_service_due", label: "Send reminder" },
  feedback: { templateKey: "feedback", label: "Send feedback form" },
};

// The icon tile follows the theme; the action button is the design's fixed colour.
const RECOMMENDATION_TONE = {
  danger: { tile: "bg-msd-brick", btn: "bg-[#F04438]" },
  warn: { tile: "bg-msd-amber", btn: "bg-[#F79009]" },
  info: { tile: "bg-msd-royal", btn: "bg-msd-btn" },
};

/**
 * Priority actions for whichever group is open in chat, shown right above
 * the composer so the advisor sees them without opening the right rail.
 * Ordered on purpose, most urgent first:
 *   1. the customer hasn't joined the WhatsApp group yet -- nothing else
 *      here matters until that's true, since the customer cannot see any
 *      of it.
 *   2. an open escalation or complaint needs resolving.
 *   3. a routine nudge toward whatever the service journey needs next.
 * Unlike EscalationBanner (gated to the Escalations bucket only), this reads
 * the group in front of the advisor regardless of which left-rail filter
 * they arrived from -- `hideEscalation` just avoids showing the same
 * escalation twice when that banner is already on screen.
 */
function RecommendationBar({ row, summary, escalation, hideEscalation, onOpenSection, inviteCustomer }) {
  const queryClient = useQueryClient();
  const { templateAvailability } = useTenantTerms();
  const sendInvite = useSendInviteReminder();
  const nextStageSenders = {
    workshop_contacts: useSendWorkshopContacts(),
    welcome: useSendWelcomeMessage(),
    pre_delivery_feedback: useSendPreDeliveryFeedback(),
    next_service_due: useSendNextServiceDue(),
    feedback: useSendFeedback(),
  };

  // Customer-not-joined used to be a recommendation here, but that made it
  // one of several equally-weighted chips when it's really a blocker: the
  // composer below now takes over entirely for that case (disabled, with
  // its own Send invite reminder control) rather than just suggesting it
  // alongside a still-usable message box. With the chat panel off there is no
  // composer, so `inviteCustomer` puts the blocker back here, first.
  // No next step is offered until the customer has joined: the journey's
  // steps are locked until then.
  const nextStep = (customerInGroup(summary?.members) && summary?.steps?.find((s) => s.state === "next")) || null;
  const nextSend = nextStep ? NEXT_STAGE_SEND[nextStep.key] : null;
  const nextSendAvailable = nextSend ? templateAvailability(nextSend.templateKey).available : false;

  const recs = [];

  if (inviteCustomer) {
    recs.push({
      key: "invite",
      tone: "warn",
      icon: "userx",
      title: "Customer hasn't joined the group",
      actionLabel: sendInvite.isPending ? "Sending…" : "Send invite reminder",
      pending: sendInvite.isPending,
      onClick: () =>
        sendInvite.mutate(Number(row), {
          onSuccess: (data) => {
            const n = data.total_sent ?? 0;
            toast.success(n > 0 ? `Invite sent to ${n} member${n === 1 ? "" : "s"}.` : "No pending members to remind.");
          },
          onError: (err) => toast.error(err?.response?.data?.detail || err?.message || "Failed to send reminder."),
        }),
    });
  }

  if (shouldShowEscalationRecommendation({
    escalation,
    openComplaints: summary?.openComplaints,
    hideEscalation,
  })) {
    recs.push({
      key: "escalation",
      tone: "danger",
      icon: "warn",
      title: "Escalation open",
      sub: escalation.reasonText || "Needs resolution",
      actionLabel: "View escalation",
      onClick: () => onOpenSection("escalation"),
    });
  } else if ((summary?.openComplaints || 0) > 0) {
    recs.push({
      key: "complaints",
      tone: "danger",
      icon: "warn",
      title: `${summary.openComplaints} complaint${summary.openComplaints === 1 ? "" : "s"} open`,
      actionLabel: "View complaints",
      onClick: () => onOpenSection("complaints"),
    });
  }

  if (nextStep) {
    const sender = nextSend && nextSendAvailable ? nextStageSenders[nextSend.templateKey] : null;
    recs.push({
      key: "next-stage",
      tone: "info",
      icon: "arrow",
      title: `Next: ${nextStep.label}`,
      actionLabel: sender ? (sender.isPending ? "Sending…" : nextSend.label) : "Open journey",
      pending: sender?.isPending,
      onClick: sender
        ? () =>
          sender.mutate(
            { row: Number(row) },
            {
              onSuccess: async () => {
                toast.success(`${nextSend.label} sent.`);
                await queryClient.invalidateQueries({ queryKey: ["groups"] });
              },
              onError: (err) => toast.error(err?.response?.data?.detail || "Failed to send."),
            }
          )
        : () => onOpenSection("journey"),
    });
  }

  if (!recs.length) return null;

  return (
    <div className="shrink-0" data-testid="desk-recommendations">
      {recs.map((r) => {
        const t = RECOMMENDATION_TONE[r.tone];
        return (
          <Bar key={r.key} icon={r.icon} tile={t.tile} title={r.title} sub={r.sub} testid={`desk-recommendation-${r.key}`}>
            <button
              type="button"
              onClick={r.onClick}
              disabled={r.pending}
              data-testid={`desk-recommendation-action-${r.key}`}
              className={cx(TBTN, t.btn)}
            >
              {r.actionLabel}
            </button>
          </Bar>
        );
      })}
    </div>
  );
}

function CenterPane({
  group,
  row,
  activeBucket,
  escalation,
  onResolveEscalation,
  stakeholders,
  summary,
  onOpenSection,
  catalog,
  onBack,
  onDetails,
  chat,
  workspace,
  className,
}) {
  const showEscalation = activeBucket === "escalations" && group && escalation;
  // The customer's second number counts (customerInGroup).
  const customerNotJoined = !customerInGroup(summary?.members);
  const deleted = isWaGroupDeleted(group);

  // With the chat panel off, the actions sit under the header (there is no
  // composer to sit above) and the workspace fills the rest. A deleted group
  // gets the read-only notice instead of suggested actions: every one sends.
  const actions = group && (
    <div className={cx("shrink-0", !chat && "pt-[10px]")}>
      {deleted ? (
        !chat && <GroupDeletedNotice group={group} className="mx-[18px]" />
      ) : (
        <RecommendationBar
          row={row}
          summary={summary}
          escalation={escalation}
          hideEscalation={showEscalation}
          onOpenSection={onOpenSection}
          inviteCustomer={!chat && customerNotJoined}
        />
      )}
      {showEscalation && (
        <EscalationBanner escalation={escalation} group={group} onResolve={onResolveEscalation} onAssign={() => { }} />
      )}
    </div>
  );

  return (
    <section
      aria-label={chat ? "Conversation" : "Vehicle"}
      className={cx(CARD, "relative flex min-h-0 min-w-0 flex-col overflow-hidden", className)}
    >
      {group && (
        <ChatHeader
          group={group}
          catalog={catalog}
          summary={summary}
          onOpenSection={onOpenSection}
          onBack={onBack}
          onDetails={chat ? onDetails : undefined}
        />
      )}
      {!group ? (
        <div className="flex flex-1 flex-col items-center justify-center px-6 text-center">
          <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-msd-soft text-msd-faint">
            <Icon name={chat ? "chat" : "car"} size={22} />
          </div>
          <h3 className="m-0 text-[16px] font-semibold text-msd-ink">
            {chat ? "No conversation selected" : "No vehicle selected"}
          </h3>
        </div>
      ) : chat ? (
        <>
          <MessageTimeline group={group} row={row} stakeholders={stakeholders} />
          {actions}
          {deleted ? (
            <GroupDeletedNotice group={group} className="mx-[18px] mb-4 shrink-0 max-[860px]:mx-[10px]" />
          ) : (
            <ChatCompose group={group} row={row} customerNotJoined={customerNotJoined} />
          )}
        </>
      ) : (
        <>
          {actions}
          {workspace}
        </>
      )}
    </section>
  );
}

function matchesNumber(row, numberFilter) {
  if (numberFilter === "all") return true;
  return String(row?.["Client Number"] || "").trim() === numberFilter;
}

// Same DateRangePicker Workshop Analytics uses (presets + single-day/range
// calendar), applied to when the group itself was created -- consistent with
// "Groups Created volume" using that same field elsewhere on the site. No
// range picked (the desk's default) means unfiltered, unlike Workshop
// Analytics which always has one.
function matchesDate(row, range) {
  if (!range?.from || !range?.to) return true;
  return inRange(parseCreatedAt(row), range);
}

function matchesSearch(item, q) {
  if (!q) return true;
  const normalizedQ = normalizeGroupName(q);
  return (
    item.customerName.toLowerCase().includes(q) ||
    item.vehicleReg.toLowerCase().includes(q) ||
    (normalizedQ && normalizeGroupName(item.vehicleReg).includes(normalizedQ)) ||
    String(item.row?.["Customer Number"] || "").toLowerCase().includes(q) ||
    String(item.escalation?.reasonText || "").toLowerCase().includes(q) ||
    String(item.escalation?.triggeringMessage || "").toLowerCase().includes(q)
  );
}

export default function ServiceDesk() {
  const { me } = useAuth();
  const { deskChat, loading: tenantLoading } = useTenantTerms();
  const { data: groupsData, isLoading } = useActiveGroups();
  const { data: shData, isLoading: sentimentLoading } = useAllSentimentHistory();
  const rows = useMemo(() => groupsData?.rows || [], [groupsData]);
  const { data: awaitingData } = useAwaitingReply();
  // Extra facts some queues need beyond the group rows themselves.
  const queueExtras = useMemo(() => ({ awaitingReplyIds: new Set(awaitingData?.group_row_ids || []) }), [awaitingData]);
  const sentimentRows = useMemo(() => shData?.rows || [], [shData]);

  const location = useLocation();
  // Arriving from Workshop analytics can ask for one queue (Customer Not Joined);
  // a name the desk doesn't have falls back to the usual one.
  const wantedBucket = location.state?.bucket;
  const [activeBucket, setActiveBucket] = useState(
    BUCKETS.some((b) => b.key === wantedBucket) ? wantedBucket : DEFAULT_BUCKET
  );
  // Arriving from a finished booking opens that group, already in the list.
  const [selectedRow, setSelectedRow] = useState(location.state?.openRow ?? openRowFromSearch(location.search));
  const [search, setSearch] = useState("");
  const [numberFilter, setNumberFilter] = useState("all");
  // Every role but the advisor can narrow the list to one advisor's groups;
  // an advisor's list already holds only their own.
  const [advisorFilter, setAdvisorFilter] = useState("all");
  // No range picked = unfiltered, unlike Workshop Analytics' DateRangePicker
  // (same component), which always has a default range.
  const [dateRange, setDateRange] = useState(null);
  const [escalationOverrides, setEscalationOverrides] = useState({});
  const [memberSummaries, setMemberSummaries] = useState({});

  const bucket = BUCKETS.find((b) => b.key === activeBucket) || BUCKETS[0];

  const baseEscalations = useMemo(
    () => deskEscalationsForMe(rows, me, sentimentRows),
    [rows, me, sentimentRows]
  );

  const escalations = useMemo(
    () => baseEscalations.map((e) => ({ ...e, ...(escalationOverrides[e.groupRowId] || {}) })),
    [baseEscalations, escalationOverrides]
  );

  const openEscalationCount = useMemo(
    () => escalations.filter((e) => e.status === "open").length,
    [escalations]
  );

  const counts = useMemo(() => {
    const out = {};
    for (const b of BUCKETS) {
      if (b.key === "escalations") out[b.key] = openEscalationCount;
      else if (b.connected) out[b.key] = b.filter(rows, me, queueExtras).length;
      else out[b.key] = 0;
    }
    return out;
  }, [rows, me, openEscalationCount, queueExtras]);

  // Only worth offering when a brand has groups on more than one number.
  const clientNumbers = useMemo(() => {
    const seen = [...new Set(rows.map((r) => String(r["Client Number"] || "").trim()).filter(Boolean))];
    return seen.sort();
  }, [rows]);

  const advisors = useMemo(() => (me?.role === "sa" ? [] : advisorOptions(rows)), [rows, me]);

  const vehicles = useMemo(() => {
    if (!bucket.connected) return [];
    const q = search.trim().toLowerCase();
    const keep = (v) =>
      matchesSearch(v, q) &&
      matchesNumber(v.row, numberFilter) &&
      matchesDate(v.row, dateRange) &&
      matchesAdvisor(v.row, advisorFilter);

    if (bucket.kind === "escalations") {
      return escalations
        .filter((e) => e.status === "open")
        .map((e) => {
          const groupRow = rows.find((r) => r._row === e.groupRowId) || null;
          return {
            id: e.groupRowId,
            customerName: e.customerName,
            vehicleReg: e.vehicleReg,
            createdAt: e.raisedAt,
            row: groupRow,
            escalation: e,
          };
        })
        .filter(keep)
        .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
    }

    return bucket
      .filter(rows, me, queueExtras)
      .map(toQueueVehicle)
      .filter(keep)
      .sort((a, b) => {
        const da = parseCreatedAt(a.row);
        const db = parseCreatedAt(b.row);
        if (da && db) return db - da;
        if (da) return -1;
        if (db) return 1;
        return b.id - a.id;
      });
  }, [bucket, rows, me, search, escalations, numberFilter, dateRange, advisorFilter, queueExtras]);

  const selectedGroup = useMemo(
    () => rows.find((r) => r._row === selectedRow) || null,
    [rows, selectedRow]
  );

  const selectedEscalation = useMemo(
    () => escalations.find((e) => e.groupRowId === selectedRow) || null,
    [escalations, selectedRow]
  );

  const { data: stakeholdersData } = useStakeholders(selectedGroup?.["Workshop Location"]);
  const stakeholders = stakeholdersData?.stakeholders || [];
  // Vehicle pictures for the list rows and the chat header.
  const { data: catalog } = useVehicleCatalog();

  const workshopLabel = me?.role === "master" ? "All Workshops" : me?.workshop_location || "All Workshops";
  const listLoading = isLoading || (bucket.kind === "escalations" && sentimentLoading);
  const sectionLabel = `${workshopLabel} · ${bucket.label} (${listLoading ? "…" : vehicles.length})`;

  const summary = useDeskSummary(selectedGroup, selectedRow, stakeholders);
  const deskEscalation = activeBucket === "escalations" ? selectedEscalation : null;

  // Layout-only state for narrow screens. Under 1280px the details column is a
  // panel that slides over the chat; under 860px the list and the chat take
  // turns, and the chat shows once a conversation is opened.
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(location.state?.openRow != null);
  const pane = chatOpen && selectedGroup ? "chat" : "list";
  useEffect(() => {
    if (!detailsOpen) return undefined;
    const key = (e) => e.key === "Escape" && setDetailsOpen(false);
    document.addEventListener("keydown", key);
    return () => document.removeEventListener("keydown", key);
  }, [detailsOpen]);

  // Journey open by default; an escalation opens its own section first.
  const [openSections, setOpenSections] = useState(["journey"]);
  useEffect(() => {
    setOpenSections(deskEscalation ? ["escalation", "journey"] : ["journey"]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRow]);
  const toggleSection = (key) =>
    setOpenSections((open) => (open.includes(key) ? open.filter((k) => k !== key) : [...open, key]));
  // A header chip focuses its section: open it, fold the rest (the journey is
  // long enough to push everything else off screen).
  const openSection = (key) => {
    setDetailsOpen(true);
    setOpenSections(deskEscalation && key !== "escalation" ? ["escalation", key] : [key]);
    requestAnimationFrame(() =>
      document.querySelector(`[data-testid="desk-section-${key}"]`)?.scrollIntoView({ behavior: "smooth", block: "start" })
    );
  };

  function handleResolveEscalation() {
    if (!selectedRow) return;
    setEscalationOverrides((prev) => ({
      ...prev,
      [selectedRow]: { status: "closed", closedAt: new Date().toISOString() },
    }));
  }

  // The brand's settings say whether the desk has a chat panel; until they
  // load, a brand with it off would see the chat appear and then vanish.
  if (tenantLoading) {
    return (
      <div className="flex min-h-0 grow items-center justify-center" data-testid="service-desk-loading">
        <Loader2 size={24} className="animate-spin text-msd-faint" />
      </div>
    );
  }

  const detailProps = {
    group: selectedGroup,
    row: selectedRow,
    escalation: deskEscalation,
    summary,
    openSections,
    onToggleSection: toggleSection,
  };

  return (
    <div
      className={cx(
        "grid min-h-0 grow grid-rows-[minmax(0,1fr)] gap-4 p-5 max-[1280px]:grid-cols-[320px_minmax(0,1fr)] max-[860px]:grid-cols-1 max-[860px]:gap-[10px] max-[860px]:p-[10px]",
        deskChat ? "grid-cols-[340px_minmax(0,1fr)_380px]" : "grid-cols-[340px_minmax(0,1fr)]",
      )}
      data-testid="service-desk"
      data-pane={pane}
    >
      <LeftRail
        buckets={BUCKETS}
        counts={counts}
        activeBucket={activeBucket}
        onSelectBucket={(k) => {
          setActiveBucket(k);
          setSelectedRow(null);
        }}
        sectionLabel={listLoading ? "Loading…" : sectionLabel}
        vehicles={vehicles}
        connected={bucket.connected}
        listKind={bucket.kind || "default"}
        selectedRow={selectedRow}
        onSelectRow={(id) => {
          setSelectedRow(id);
          setChatOpen(true);
        }}
        search={search}
        onSearch={setSearch}
        clientNumbers={clientNumbers}
        numberFilter={numberFilter}
        onNumberFilter={setNumberFilter}
        advisors={advisors}
        advisorFilter={advisorFilter}
        onAdvisorFilter={setAdvisorFilter}
        dateRange={dateRange}
        onDateRange={setDateRange}
        catalog={catalog}
        className={pane === "chat" ? "max-[860px]:hidden" : ""}
      />
      <CenterPane
        group={selectedGroup}
        row={selectedRow}
        activeBucket={activeBucket}
        escalation={selectedEscalation}
        onResolveEscalation={handleResolveEscalation}
        stakeholders={stakeholders}
        summary={summary}
        onOpenSection={openSection}
        catalog={catalog}
        onBack={() => setChatOpen(false)}
        onDetails={() => setDetailsOpen((open) => !open)}
        chat={deskChat}
        workspace={!deskChat && <Workspace {...detailProps} />}
        className={pane === "list" ? "max-[860px]:hidden" : ""}
      />
      {/* The details column. RightRail fills this box; under 1280px the box is
          a panel fixed to the right edge that slides in over the chat, with
          its own close row. Without the chat panel the workspace holds all of
          this instead. */}
      {deskChat && (
        <div
          className={cx(
            "flex min-h-0 min-w-0 flex-col [&>*:last-child]:!h-auto [&>*:last-child]:!min-h-0 [&>*:last-child]:!w-full [&>*:last-child]:!flex-1",
            "max-[1280px]:fixed max-[1280px]:bottom-4 max-[1280px]:right-4 max-[1280px]:top-4 max-[1280px]:z-[45] max-[1280px]:w-[min(400px,calc(100vw-32px))] max-[1280px]:overflow-hidden max-[1280px]:rounded-[20px] max-[1280px]:bg-msd-surface max-[1280px]:shadow-msd-pop max-[1280px]:transition-[transform,visibility] max-[1280px]:duration-200 motion-reduce:transition-none",
            "max-[1280px]:[&>*:last-child]:!rounded-none max-[1280px]:[&>*:last-child]:!shadow-none",
            !detailsOpen && "max-[1280px]:invisible max-[1280px]:translate-x-[calc(100%+24px)]",
          )}
          data-testid="desk-details-panel"
        >
          <div className="hidden shrink-0 items-center justify-between gap-3 px-4 py-3 shadow-[0_1px_0_var(--line)] max-[1280px]:flex">
            <b className="min-w-0 truncate text-msd-ink">
              {selectedGroup?.["Group Name"] || selectedGroup?.["Customer Name"] || "Details"}
            </b>
            <IconBtn icon="x" label="Close details" size={18} stroke={2} onClick={() => setDetailsOpen(false)} />
          </div>
          <RightRail {...detailProps} />
        </div>
      )}
    </div>
  );
}
